import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LogOut, ExternalLink, Trash2, Plus, CreditCard, Tag, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";

const emptyCoupon = { code: "", discount_type: "percent", value: 10, duration_months: 3, max_redemptions: null, active: true, description: "" };

export default function SuperAdmin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState(null);
  const [subs, setSubs] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [couponDialog, setCouponDialog] = useState(null); // null | "new" | coupon object

  const load = async () => {
    try {
      const [t, s, sub, c] = await Promise.all([
        api.get("/admin/tenants"), api.get("/admin/stats"),
        api.get("/admin/subscriptions"), api.get("/admin/coupons"),
      ]);
      setTenants(t.data); setStats(s.data); setSubs(sub.data); setCoupons(c.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => { load(); }, []);

  const updateTenant = async (id, patch) => { try { await api.put(`/admin/tenants/${id}`, patch); toast.success("Updated"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const deleteTenant = async (id) => { if (!window.confirm("Delete this tenant?")) return; try { await api.delete(`/admin/tenants/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const saveCoupon = async (data) => {
    try {
      if (data.coupon_id) await api.put(`/admin/coupons/${data.coupon_id}`, data);
      else await api.post("/admin/coupons", data);
      toast.success("Coupon saved"); setCouponDialog(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteCoupon = async (id) => { if (!window.confirm("Delete coupon?")) return; try { await api.delete(`/admin/coupons/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const mrrTotal = subs.reduce((sum, s) => sum + (s.mrr || 0), 0);

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <header className="bg-white border-b border-[#E5E2DC] px-6 py-4 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2.5">
            <Logo theme="black" height={26} />
            <span className="font-semibold text-[#5C5856]">Master Portal</span>
          </Link>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-[#5C5856]" data-testid="super-admin-email">{user?.email}</span>
          <Button onClick={() => { logout(); navigate("/"); }} variant="ghost" size="sm" data-testid="super-admin-logout"><LogOut className="w-4 h-4" /></Button>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-8">
        <h1 className="font-serif-marketing text-4xl text-[#2A2626]">Master Portal</h1>
        <p className="text-[#5C5856] mt-1">Every church, every subscription, every coupon — all in one place.</p>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <Card className="p-5"><p className="text-xs uppercase text-[#5C5856] flex items-center gap-1"><UsersIcon className="w-3 h-3" /> Total tenants</p><p className="text-3xl font-bold mt-1" data-testid="stat-total">{stats.total_tenants}</p></Card>
            <Card className="p-5"><p className="text-xs uppercase text-[#5C5856]">Active</p><p className="text-3xl font-bold mt-1 text-[#6B8E6B]" data-testid="stat-active">{stats.active_tenants}</p></Card>
            <Card className="p-5"><p className="text-xs uppercase text-[#5C5856]">Premium subscribers</p><p className="text-3xl font-bold mt-1 text-[#2A2626]" data-testid="stat-premium">{stats.premium_tenants}</p></Card>
            <Card className="p-5"><p className="text-xs uppercase text-[#5C5856] flex items-center gap-1"><CreditCard className="w-3 h-3" /> MRR (est.)</p><p className="text-3xl font-bold mt-1" data-testid="stat-mrr">${mrrTotal}</p></Card>
          </div>
        )}

        <Tabs defaultValue="tenants" className="mt-8">
          <TabsList className="bg-white border border-[#E5E2DC]">
            <TabsTrigger value="tenants" data-testid="master-tab-tenants">Tenants</TabsTrigger>
            <TabsTrigger value="subscriptions" data-testid="master-tab-subs">Subscriptions</TabsTrigger>
            <TabsTrigger value="coupons" data-testid="master-tab-coupons">Coupons</TabsTrigger>
          </TabsList>

          <TabsContent value="tenants" className="mt-6 space-y-3">
            {tenants.map((t) => (
              <Card key={t.tenant_id} className="p-5 flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid={`tenant-row-${t.tenant_id}`}>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-lg">{t.name}</h3>
                    <Badge className={t.tier !== "connect" ? "bg-[#2A2626]" : "bg-[#F0EBE1] text-[#5C5856]"}>{(t.tier || "connect").toUpperCase()}</Badge>
                    {!t.active && <Badge variant="destructive">SUSPENDED</Badge>}
                  </div>
                  <p className="text-xs text-[#5C5856] mt-1">/{t.slug} · {new Date(t.created_at).toLocaleDateString()}</p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <Select value={t.tier || "connect"} onValueChange={(v) => updateTenant(t.tenant_id, { tier: v })}>
                    <SelectTrigger className="h-9 w-32" data-testid={`tenant-plan-${t.tenant_id}`}><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="connect">Connect</SelectItem><SelectItem value="grow">Grow</SelectItem><SelectItem value="multiply">Multiply</SelectItem></SelectContent>
                  </Select>
                  <div className="flex items-center gap-2">
                    <Switch checked={t.active} onCheckedChange={(v) => updateTenant(t.tenant_id, { active: v })} data-testid={`tenant-active-${t.tenant_id}`} />
                    <span className="text-xs text-[#5C5856]">Active</span>
                  </div>
                  <Link to={`/kiosk/${t.slug}`} target="_blank"><Button variant="outline" size="sm" className="rounded-full"><ExternalLink className="w-4 h-4" /></Button></Link>
                  <Button variant="ghost" size="sm" onClick={() => deleteTenant(t.tenant_id)} data-testid={`tenant-delete-${t.tenant_id}`}><Trash2 className="w-4 h-4 text-[#B23A48]" /></Button>
                </div>
              </Card>
            ))}
            {tenants.length === 0 && <Card className="p-12 text-center text-[#5C5856]">No tenants yet.</Card>}
          </TabsContent>

          <TabsContent value="subscriptions" className="mt-6">
            <Card className="overflow-hidden">
              <table className="w-full text-sm" data-testid="subs-table">
                <thead className="bg-[#F0EBE1]"><tr className="text-left">
                  <th className="px-4 py-3 font-semibold">Church</th>
                  <th className="px-4 py-3 font-semibold">Plan</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Payment method</th>
                  <th className="px-4 py-3 font-semibold">MRR</th>
                  <th className="px-4 py-3 font-semibold">Trial ends</th>
                </tr></thead>
                <tbody>
                  {subs.map((s) => (
                    <tr key={s.tenant_id} className="border-t border-[#E5E2DC]" data-testid={`sub-row-${s.tenant_id}`}>
                      <td className="px-4 py-3"><p className="font-medium">{s.name}</p><p className="text-xs text-[#5C5856]">/{s.slug}</p></td>
                      <td className="px-4 py-3"><Badge className={(s.tier || s.effective_plan) !== "connect" ? "bg-[#2A2626]" : "bg-[#F0EBE1] text-[#5C5856]"}>{(s.tier || s.effective_plan || "connect").toUpperCase()}</Badge></td>
                      <td className="px-4 py-3"><span className="text-xs px-2 py-1 rounded-full bg-[#F0EBE1]">{s.subscription_status}</span></td>
                      <td className="px-4 py-3 text-xs text-[#5C5856]">{s.payment_method ? `${s.payment_method.brand} •••• ${s.payment_method.last4}` : <span className="italic">no card on file</span>}</td>
                      <td className="px-4 py-3 font-semibold">${s.mrr}</td>
                      <td className="px-4 py-3 text-xs text-[#5C5856]">{s.trial_ends_at ? new Date(s.trial_ends_at).toLocaleDateString() : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
            <p className="text-xs text-[#5C5856] mt-3 italic">Live subscription data synced from Stripe. Manage plans, invoices, and payment methods in your Stripe Dashboard.</p>
          </TabsContent>

          <TabsContent value="coupons" className="mt-6 space-y-3">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-[#5C5856]">Create promo codes for discounts or extended trials.</p>
              <Button onClick={() => setCouponDialog("new")} className="bg-[#2A2626] hover:bg-[#1A1717] text-white rounded-full" data-testid="coupon-new"><Plus className="w-4 h-4 mr-1" /> New coupon</Button>
            </div>
            {coupons.map((c) => (
              <Card key={c.coupon_id} className="p-5 flex items-center justify-between gap-4" data-testid={`coupon-${c.code}`}>
                <div className="flex items-center gap-3">
                  <Tag className="w-5 h-5 text-[#2A2626]" />
                  <div>
                    <div className="flex items-center gap-2">
                      <code className="font-mono font-bold text-[#2A2626] bg-[#F0EBE1] px-2 py-0.5 rounded">{c.code}</code>
                      {!c.active && <Badge variant="secondary">Inactive</Badge>}
                    </div>
                    <p className="text-xs text-[#5C5856] mt-1">
                      {c.discount_type === "percent" && `${c.value}% off for ${c.duration_months} month${c.duration_months > 1 ? "s" : ""}`}
                      {c.discount_type === "flat" && `$${c.value} off for ${c.duration_months} month${c.duration_months > 1 ? "s" : ""}`}
                      {c.discount_type === "trial_extend" && `Extends trial by ${c.value} days`}
                      {c.max_redemptions && ` · ${c.redemptions || 0}/${c.max_redemptions} redeemed`}
                      {c.description && ` · ${c.description}`}
                    </p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setCouponDialog(c)} data-testid={`coupon-edit-${c.code}`}>Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteCoupon(c.coupon_id)} data-testid={`coupon-delete-${c.code}`}><Trash2 className="w-4 h-4 text-[#B23A48]" /></Button>
                </div>
              </Card>
            ))}
            {coupons.length === 0 && <Card className="p-12 text-center text-[#5C5856]">No coupons yet. Create your first promo code above.</Card>}
          </TabsContent>
        </Tabs>
      </main>

      <CouponDialog open={!!couponDialog} initial={couponDialog === "new" ? emptyCoupon : couponDialog} onClose={() => setCouponDialog(null)} onSave={saveCoupon} />
    </div>
  );
}

function CouponDialog({ open, initial, onClose, onSave }) {
  const [data, setData] = useState(initial || emptyCoupon);
  useEffect(() => { if (open) setData(initial || emptyCoupon); }, [open, initial]);
  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="coupon-dialog">
        <DialogHeader><DialogTitle>{data.coupon_id ? "Edit coupon" : "New coupon"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Code</Label><Input value={data.code} onChange={(e) => setData({ ...data, code: e.target.value.toUpperCase() })} placeholder="LAUNCH50" className="h-10 mt-1 font-mono" data-testid="coupon-code" /></div>
          <div><Label>Discount type</Label>
            <Select value={data.discount_type} onValueChange={(v) => setData({ ...data, discount_type: v })}>
              <SelectTrigger className="h-10 mt-1" data-testid="coupon-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="percent">Percent off</SelectItem>
                <SelectItem value="flat">Flat $ off</SelectItem>
                <SelectItem value="trial_extend">Extend free trial (days)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{data.discount_type === "percent" ? "% off" : data.discount_type === "flat" ? "$ off" : "Days"}</Label><Input type="number" value={data.value} onChange={(e) => setData({ ...data, value: parseFloat(e.target.value) || 0 })} className="h-10 mt-1" data-testid="coupon-value" /></div>
            {data.discount_type !== "trial_extend" && (
              <div><Label>Duration (months)</Label><Input type="number" value={data.duration_months} onChange={(e) => setData({ ...data, duration_months: parseInt(e.target.value) || 1 })} className="h-10 mt-1" /></div>
            )}
          </div>
          <div><Label>Max redemptions (optional)</Label><Input type="number" value={data.max_redemptions || ""} onChange={(e) => setData({ ...data, max_redemptions: e.target.value ? parseInt(e.target.value) : null })} placeholder="Unlimited" className="h-10 mt-1" /></div>
          <div><Label>Description</Label><Input value={data.description} onChange={(e) => setData({ ...data, description: e.target.value })} placeholder="Launch promotion" className="h-10 mt-1" /></div>
          <div className="flex items-center gap-2"><Switch checked={data.active} onCheckedChange={(v) => setData({ ...data, active: v })} /><Label>Active</Label></div>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
            <Button onClick={() => onSave(data)} className="bg-[#2A2626] hover:bg-[#1A1717] text-white rounded-full" data-testid="coupon-save">Save</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
